import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ModuleLoader,
  useConfig,
  useDocumentTitle,
} from "@eis-poc/lib-discover-medical-mfes";
import {
  Flex,
  Icon,
  TabPanel,
  Type,
} from "discover.medical.react-component-library";
import debounce from "lodash/debounce";

import ReactDOM from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import AboutTab from "../../components/AboutTab";
import AlgorithmsTab from "../../components/AlgorithmsTab";
import ChecklistTab from "../../components/ChecklistTab";
import CitationDialog from "../../components/CitationDialog";
import CommonComponents from "../../components/CommonComponents";
import ContentBlock from "../../components/ContentBlock";
import ContentHeader from "../../components/ContentHeader";
import ContentHeaderBar from "../../components/ContentHeaderBar";
import ContentNote from "../../components/ContentNote";
import ContentPrintDialog from "../../components/ContentPrintDialog";
import ContentRightRail from "../../components/ContentRightRail";
import ContentRightRailDDTools from "../../components/ContentRightRailDDTools";
import RightRailRelatedTopics from "../../components/RightRailRelatedTopics";
import ContentSectionSkeleton from "../../components/ContentSectionSkeleton";
import ContentShareDialog from "../../components/ContentShareDialog";
import ContentTableDialog from "../../components/ContentTableDialog";
import ContentHFCDialog from "../../components/ContentHFCDialog";
import ContentToc from "../../components/ContentToc";
import DetailsBlock from "../../components/DetailsBlock";
import DialogLightBoxImage from "../../components/DialogLightBoxImage";
import DialogLightBoxVideo from "../../components/DialogLightBoxVideo";
import { DrugMobileTocTabs } from "../../components/DrugMobileTocTabs";
import EvidenceBottomSheet from "../../components/EvidenceBottomSheet";
import FindOnPage from "../../components/FindOnPage";
import FollowDialog from "../../components/FollowDialog";
import MainSpinner from "../../components/MainSpinner";
import MediaGallery from "../../components/MediaGallery";
import NoDataMessage from "../../components/NoDataMessage";
import Notification from "../../components/Notification";
import PreviousNextSection from "../../components/PreviousNextSection";
import PrintLegalText from "../../components/PrintingLegalText";
import TablesTab from "../../components/TablesTab";
import TopicUpdatesDialog from "../../components/TopicUpdatesDialog";
import UpdatesTab from "../../components/UpdatesTab";
import UserInactivityDialog from "../../components/UserInactivityDialog";
import { drugMobileTocTabTypes } from "../../constants/drug-mobile-toc-tab-types";
import { tabTypes } from "../../constants/tab-types";
import useContent from "../../hooks/useContent";
import useDrugMobileTocTabs from "../../hooks/useDrugMobileTocTabs";
import useFixedHeader from "../../hooks/useFixedHeader";
import useGetCurrentDmxDrugTab from "../../hooks/useGetCurrentDmxDrugTab";
import { useImages } from "../../hooks/useImages";
import { useInactivityTracker } from "../../hooks/useInactivityTracker";
import {
  CTA_EVENT_TYPE,
  CTA_TRIGGER_ACTION,
  useLaunchCTA,
} from "../../hooks/useLaunchCTA";
import { useMediaDialogs } from "../../hooks/useMediaDialogs";
import useMerativeLink from "../../hooks/useMerativeLink";
import usePrint from "../../hooks/usePrint";
import { useScrollElement } from "../../hooks/useScrollElement";
import useShare from "../../hooks/useShare";
import { useTables } from "../../hooks/useTables";
import useVideo from "../../hooks/useVideo";
import useWindowSize from "../../hooks/useWindowSize";
import useHFC from "../../hooks/useHFC";
import { useDispatch } from "../../store";
import {
  bookmarkContent,
  collectItemViewsEvent,
  followContent,
  getCloudinaryToken,
  getContent,
  getDetailsSectionByHash,
  getFullTopic,
  getPrintDetailsSections,
  getPrintSections,
  getSectionByHash,
  notifyCopyToClipboard,
  postItemViews,
  setActiveTocItem,
  setActiveVideo,
  unbookmarkContent,
  unfollowContent,
} from "../../store/actions";
import {
  affordances,
  logJumpLinkClicked,
  logTableViewed,
  logTocClicked,
  logTopicHiddenViewed,
  logTopicCiteClicked,
  logTopicPrintClicked,
  logTopicSectionViewed,
  logTopicShareClicked,
} from "../../util/analytics";
import Epic from "../../util/epic";
import { findOnPageModes, getFindOnPageMode } from "../../util/findOnPageModes";
import { getDetailsSectionPath } from "../../util/getDetailsSectionPath";
import { getDimensionsById } from "../../util/getDimensionsById";
import { getPreviousNextDetailsSectionId } from "../../util/getPreviousNextDetailsSectionId";
import { getSectionName } from "../../util/getSectionName";
import {
  pixelsPerRem,
  reservedSpaceHeader,
  spaceBetweenSections,
  tocOffset,
} from "../../util/mediaConstants";
import { scrollToElementById, setHash } from "../../util/scrollToElement";
import { setTableWidths } from "../../util/setTableWidths";
import { shouldRenderExtraContentSection } from "../../util/shouldRenderExtraContentSection";
import { handleTabView } from "../../util/tabViewAnalytics";
import { useEventBus } from "../../util/useEventBus";
import { copyToClipboard } from "../../util/copyToClipboard";
import {
  ContentBlockWrapper,
  ContentPageRightRail,
  FlexChecklistInfoColumn,
  FlexContainer,
  FlexContentColumn,
  FlexRightRailColumn,
  FlexTocColumn,
  FlexUpdates,
  StyledInfoText,
  TocMobileCloseButton,
  TocMobileNameHolder,
  TocMobileOverlay,
  TocMobileWrapper,
  TocWrapperDiv,
  TopicMenuButton,
  Wrapper,
} from "./_.styled";
import useEventBusJail from "../../hooks/useEventBusJail";
import useContentLinks from "../../hooks/useContentLinks";

export const handleSectionViews = ({
  activeSectionTitle,
  pageTitle,
  metadataAn,
  prettyPubType,
  userExperience,
  dispatch,
  getSearchTerm,
  getLastUpdateDate,
  updates,
  isTracking,
}) => {
  logTopicSectionViewed({
    title: pageTitle,
    sectionTitle: activeSectionTitle,
    pubType: prettyPubType,
    recordAn: metadataAn,
    inDepth: false,
  });

  if (
    userExperience?.shouldTrackUsage &&
    userExperience?.events?.OPEN_SECTION &&
    isTracking &&
    pageTitle
  ) {
    // This slight delay is needed to preserver first instance of OPEN_SECTION
    // Otherwise userHistory.itemviews is erased from OPEN_ITEM event
    // Argument can be made to remove
    // - POST_ITEM_VIEWS
    // - POST_ITEM_VIEWS_SUCCESS
    // - POST_ITEM_VIEWS_ERROR
    // As those values are not being used in any meaningful way.
    setTimeout(() => {
      collectItemViewsEvent(dispatch, [
        {
          eventType: "OPEN_SECTION",
          itemName: pageTitle,
          sectionName: activeSectionTitle,
          prevSearchTerm: getSearchTerm(),
          itemPublicationDate: getLastUpdateDate(updates),
        },
      ]);
    }, 100);
  }
};

const ContentPage = ({ slugPubType, slugTitle, tabType, pathSectionId }) => {
  const location = useLocation();
  const { pathname, hash } = location;
  const history = useHistory();
  const {
    header,
    tabs,
    toc,
    updates,
    contentSections,
    contentSectionCache = [],
    drugDetails,
    detailsSectionCache = [[{}]],
    activeDetailsSectionIndex = 0,
    activeDetailsSubSectionIndex = 0,
    idToSectionMap,
    transformData,
    referenceData,
    headerTools,
    subscription,
    ancillaryData,
    metadata,
    multimedia,
    pubTypeLogos,
    isLoading,
    hasData,
    noDataMessage,
    about,
    workplaceData,
    breadcrumbData,
    userExperience,
    checklist,
    referenceSectionId,
    careInterventionsSectionId,
    isBookmarking,
    isFollowing,
    notification,
    activeSectionIndex,
    userHistory,
    nextSectionHash,
    previousSectionHash,
    lastHash,
    isContentFetching,
    hasContentFetched,
    isDetailsSectionFetching,
    hasPrintFailed,
    isFullTopicLoading,
    isFullTopicLoaded,
    fullTopicLoadingError,
    isFullTopicPreprocessing,
    fullTopicPreprocessingError,
    loe,
    contentHidden,
    cloudinaryToken,
    activeTocItem,
    loadedSection,
    tablesContent,
    styleConfig,
    pdfFiles,
    crossContentReferenceLinks,
  } = useContent(slugPubType, slugTitle, tabType, pathSectionId);

  const isValidSplitContent = idToSectionMap && userExperience?.isSplitContent;
  const content = isValidSplitContent ? contentSectionCache : contentSections;
  const detailsContent =
    detailsSectionCache?.[activeDetailsSectionIndex]?.[
      activeDetailsSubSectionIndex
    ]?.data;
  const {
    previousDetailsSectionId,
    nextDetailsSectionId,
  } = getPreviousNextDetailsSectionId(
    drugDetails,
    activeDetailsSectionIndex,
    activeDetailsSubSectionIndex
  );
  const targetId = hash.replace("#", "");
  const { setDocumentTitle } = useDocumentTitle();
  const { isHeaderFixed } = useFixedHeader();
  const dispatch = useDispatch();
  const pageTitle = header?.title?.label;
  const tabTitle = header?.pageTitle?.label || pageTitle;
  const prettyPubType = header?.prettyPubtype;
  const metadataAn = metadata?.an;
  const activeSectionTitle = contentSectionCache?.[activeSectionIndex]?.title;
  const { subscriptionOptions } = subscription?.subscriptionModal || [];
  const [isFollowDialogOpen, toggleFollowDialog] = useState(false);
  const [isCitationDialogOpen, setIsCitationDialogOpen] = useState(false);
  const [isBoxedWarningOpen, setIsBoxedWarningOpen] = useState(false);
  const [isUpdatesDialogOpen, setIsUpdatesDialogOpen] = useState(false);
  const [isMobileTocOpen, setMobileTocOpen] = useState(false);
  const configData = useConfig();
  const { puaId } = configData?.header?.userDetails || {};
  const { productCode, productName } = configData?.productInfo || {};
  const adminWidgetConfig = configData?.mfes?.["PlatformContentAdminWidget"];
  const isPreviewMode = !!userExperience.isPreviewMode;
  const isNonFreePreview = isPreviewMode && !userExperience.isFreeArticle;
  const topicBaseUrl = `/${slugPubType}/${slugTitle}`;
  const isDetailsTab = tabType === tabTypes.DETAILS;
  const findOnPageMode = getFindOnPageMode(tabType, productCode, slugPubType);

  const {
    windowSize,
    isMobile: isMobileFn,
    isTablet: isTabletFn,
    isDesktop: isDesktopFn,
  } = useWindowSize();
  const isMobile = isMobileFn();
  const isTablet = isTabletFn();
  const isDesktop = isDesktopFn();

  const disablePrint =
    (hasPrintFailed && notification?.isOpen) ||
    isContentFetching ||
    isDetailsSectionFetching;

  const {
    isTracking,
    setIsTracking,
    isInactive,
    setIsInactive,
  } = useInactivityTracker(
    pageTitle,
    productCode,
    userExperience?.shouldTrackUsage,
    userExperience?.userInactivityTimeout
  );

  useContentLinks(slugPubType, pageTitle, metadata?.an);

  const { currentDmxDrugTab } = useGetCurrentDmxDrugTab(tabType, styleConfig);

  const shouldRenderFindOnPage =
    (userExperience?.findInTopic || userExperience?.findInSection) &&
    (!tabType || isDetailsTab);

  const eventBus = useEventBus();
  const { launchCTA } = useLaunchCTA({ eventBus });

  const handleCopyToClipboard = (
    selectionRef,
    successMessage,
    errorMessage
  ) => {
    try {
      const copyToClipboardText = selectionRef.current.innerHTML;
      copyToClipboard(copyToClipboardText);
      notifyCopyToClipboard(dispatch, true, successMessage);
    } catch (error) {
      notifyCopyToClipboard(dispatch, false, errorMessage);
    }
  };

  const handleBookmarkActions = useCallback(
    (bookmarkAction) => {
      if (puaId) {
        bookmarkAction(dispatch, slugPubType, slugTitle);
      } else {
        launchCTA({
          type: CTA_EVENT_TYPE.ARTICLE_SUBSCRIPTION,
          userAction: CTA_TRIGGER_ACTION.BOOKMARK_ICON_CLICK,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [puaId, dispatch, launchCTA, slugPubType, slugTitle]
  );

  const getFollowToastMessage = useCallback(
    (localSubscriptionType) => {
      const { toastText } =
        subscriptionOptions?.find(
          (option) => option.subscriptionType === localSubscriptionType
        ) || "";
      return toastText;
    },
    [subscriptionOptions]
  );

  const handleFollowIconClick = useCallback(() => {
    if (!puaId) {
      return launchCTA({
        type: CTA_EVENT_TYPE.ARTICLE_SUBSCRIPTION,
        userAction: CTA_TRIGGER_ACTION.FOLLOW_ICON_CLICK,
      });
    }
    if (isPreviewMode) {
      return launchCTA({ userAction: CTA_TRIGGER_ACTION.FOLLOW_ICON_CLICK });
    }

    toggleFollowDialog(true);
  }, [puaId, isPreviewMode, launchCTA]);

  const handleFollowActions = useCallback(
    (subscriptionType) => {
      if (!isFollowing) {
        followContent(
          dispatch,
          slugPubType,
          slugTitle,
          subscriptionType,
          getFollowToastMessage(subscriptionType)
        );
        toggleFollowDialog(false);
      }
    },
    [dispatch, getFollowToastMessage, isFollowing, slugPubType, slugTitle]
  );

  const handleUnfollowActions = useCallback(() => {
    if (isFollowing) {
      return;
    }

    if (isPreviewMode) {
      return launchCTA({
        userAction: CTA_TRIGGER_ACTION.FOLLOW_ICON_CLICK,
      });
    }

    unfollowContent(dispatch, slugPubType, slugTitle);
  }, [dispatch, isPreviewMode, launchCTA, isFollowing, slugPubType, slugTitle]);

  const handleGetSectionName = useCallback(
    () => getSectionName(breadcrumbData),
    [breadcrumbData]
  );

  const {
    handleSetCurrentImage,
    handleNextImage,
    handlePrevImage,
    currentImage,
  } = useImages(multimedia?.images?.length);

  const {
    handleSetCurrentVideo,
    handleNextVideo,
    handlePrevVideo,
    currentVideo,
  } = useVideo(multimedia?.videos?.length);

  const {
    carouselImageModal: { carouselImageModalState, carouselImageModalHandle },
    fullSizeImageModal: { fullSizeImageModalState, fullSizeImageModalHandle },
    carouselVideoModal: { carouselVideoModalState, carouselVideoModalHandle },
    affordance: { affordanceState, affordanceHandle },
  } = useMediaDialogs();

  const { hasDrugMobileTocTabs, drugMobileTocActiveTab } = useDrugMobileTocTabs(
    tabType,
    tabs
  );

  const openImageHandler = useCallback(
    (index = 0, affordance) => {
      if (isNonFreePreview) {
        return launchCTA({ userAction: CTA_TRIGGER_ACTION.IMAGE_CLICK });
      }
      handleSetCurrentImage(index);
      carouselImageModalHandle();
      affordanceHandle(affordance);
    },
    [
      isNonFreePreview,
      launchCTA,
      carouselImageModalHandle,
      handleSetCurrentImage,
      affordanceHandle,
    ]
  );

  const openAlgorithmCTA = useCallback(
    () => launchCTA({ userAction: CTA_TRIGGER_ACTION.ALGORITHM_CLICK }),
    [launchCTA]
  );

  const openVideoHandler = useCallback(
    (index = 0, affordance) => {
      if (isNonFreePreview) {
        return launchCTA({ userAction: CTA_TRIGGER_ACTION.VIDEO_CLICK });
      }

      handleSetCurrentVideo(index);
      carouselVideoModalHandle();
      affordanceHandle(affordance);
    },
    [
      isNonFreePreview,
      launchCTA,
      carouselVideoModalHandle,
      handleSetCurrentVideo,
      affordanceHandle,
    ]
  );

  const openTableCTA = useCallback(
    () => launchCTA({ userAction: CTA_TRIGGER_ACTION.TABLE_CLICK }),
    [launchCTA]
  );

  const getLastUpdateDate = (lastUpdates) => {
    if (lastUpdates?.length) {
      const lastUpdateDate = lastUpdates[0];
      return new Date(
        lastUpdateDate.badge
          ? lastUpdateDate.badge.timestamp
          : lastUpdateDate.timestamp
      ).toISOString();
    }
    return "";
  };

  // We want to pull Ids for all content sections and any sub sections contained within those
  const getAllSectionIds = (sections = [], allSections = []) => {
    sections.forEach((section) => {
      if (section?.id) {
        allSections.push(section.id);
        if (section.subSections) {
          getAllSectionIds(section.subSections, allSections);
        }
      }
    });
    return allSections;
  };

  // set to empty string for hl7
  const getSearchTerm = () => {
    //TODO: update to properly derive most recent search term from state (TBD)
    return "";
  };

  const setTocOnScrollHelper = useCallback(
    (localSectionOffsets) => {
      let firstSectionTop = -1;
      const windowYPosition = window.scrollY;
      let scrollPosition = 0;
      if (isDesktop) {
        scrollPosition =
          windowYPosition + (reservedSpaceHeader.lg + tocOffset) * pixelsPerRem;
      } else {
        const breadcrumbHeight =
          document.getElementById("content-breadcrumb")?.clientHeight / 16 || 0;
        scrollPosition =
          windowYPosition +
          (reservedSpaceHeader.md +
            tocOffset +
            breadcrumbHeight -
            spaceBetweenSections) *
            pixelsPerRem;
      }
      const selectedSection = localSectionOffsets.find((section, idx) => {
        if (idx === 0) {
          firstSectionTop = section.offsetTop;
        }
        return (
          scrollPosition >= section.offsetTop &&
          scrollPosition < section.offsetBottom
        );
      });

      if (selectedSection && selectedSection.id) {
        setActiveTocItem(dispatch, selectedSection.id);
      } else if (scrollPosition < firstSectionTop) {
        // If we don't have a matched section, and we're above the first section, select first section
        // This handles things like 'Back to Top'
        setActiveTocItem(dispatch, localSectionOffsets[0].id);
      } else {
        //There is nothing to do here so Sonar be happy please
      }
    },
    [dispatch, isDesktop]
  );

  // Calculate section offsets to determine what to highlight in ToC

  const sectionContents =
    isValidSplitContent && contentSectionCache.length > 0
      ? [contentSectionCache?.[activeSectionIndex]]
      : contentSections;

  const calculateOffsets = debounce((val) => {
    const sectionOffsetsRef = getAllSectionIds(sectionContents).map((id) => {
      const { offsetTop, offsetBottom } = getDimensionsById(id);
      return {
        id,
        offsetTop,
        offsetBottom,
      };
    });

    setTocOnScrollHelper(sectionOffsetsRef);
  }, 250);

  const handleScroll = () => {
    calculateOffsets();
  };

  useLayoutEffect(() => {
    // Storing section offsets so that its not calculated
    // every time during scroll event for improved performance
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [contentSections, isHeaderFixed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    //validate that the requested tab (tabType) exists in the content.  If it doesn't, change the tabType to undefined (==>"TOPIC").
    const tab = tabType ? `/${tabType}` : "";
    const path = `/${slugPubType}/${slugTitle}${tab}`.toLowerCase();
    if (tab && tabs && !tabs.find((e) => e.path.toLowerCase() === path)) {
      history.push(`/${slugPubType}/${slugTitle}`);
    }
  }, [tabs, slugPubType, slugTitle, tabType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle tab view change when switching between tabs
  const tabTypeRef = useRef(null); // This need set to null because content tab defaults to undefined
  useEffect(() => {
    if (hasContentFetched && tabTypeRef.current !== tabType) {
      tabTypeRef.current = tabType;

      // This logic is needed to navigate from mmx drug quick answer tab to details tab
      // if the user has not visited drug details before, there is no activeSectionId so we need to fetch the entire content
      // else, we go get only the section data requested in the path
      if (!isNonFreePreview && tabType === tabTypes.DETAILS) {
        if (!drugDetails?.activeSectionId) {
          getContent(
            dispatch,
            slugPubType,
            slugTitle,
            tabType,
            undefined, //details tab has no section id in hash
            pathSectionId
          );
        } else {
          getDetailsSectionByHash({
            dispatch,
            slugPubType,
            slugTitle,
            detailsSectionCache,
            id:
              pathSectionId ?? drugDetails.toc?.[0]?.subSection?.[0]?.sectionId,
            isNonFreePreview,
          });
        }
      }

      handleTabView(tabType, {
        pubType: prettyPubType,
        title: pageTitle,
        recordAn: metadataAn,
        isFreePreview: userExperience?.isPreviewMode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabType, hasContentFetched, dispatch]);

  useEffect(() => {
    if (isDetailsTab && !isNonFreePreview && pageTitle) {
      logTopicSectionViewed({
        title: pageTitle,
        sectionTitle: pathSectionId,
        pubType: prettyPubType,
        recordAn: metadataAn,
        inDepth: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabType, pathSectionId, pageTitle]);

  useEffect(
    () => {
      if (isValidSplitContent && !tabType) {
        handleSectionViews({
          activeSectionTitle,
          pageTitle,
          metadataAn,
          prettyPubType,
          userExperience,
          dispatch,
          getSearchTerm,
          getLastUpdateDate,
          updates,
          isTracking,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageTitle, isValidSplitContent, activeSectionTitle, tabType]
  );

  useEffect(() => {
    if (tabTitle) {
      setDocumentTitle([tabTitle]);
    }
  }, [tabTitle, setDocumentTitle]);

  // log an event when a hidden topic is attempted to be viewed
  useEffect(() => {
    if (contentHidden) {
      logTopicHiddenViewed({ pathname });
    }
  }, [pathname, contentHidden]);

  const itemviewsTimerRef = useRef();
  const itemviewsRef = useRef();
  useEffect(() => {
    itemviewsRef.current = userHistory.itemviews;
    if (itemviewsRef.current?.length && !itemviewsTimerRef.current) {
      itemviewsTimerRef.current = setTimeout(() => {
        if (itemviewsRef.current?.length) {
          postItemViews(
            dispatch,
            slugPubType,
            slugTitle,
            itemviewsRef.current,
            eventBus
          );
        }
        itemviewsTimerRef.current = null;
      }, 20000);
    }
  }, [dispatch, userHistory.itemviews, eventBus]); // eslint-disable-line react-hooks/exhaustive-deps

  const shouldTrackOpenItem = () => {
    return (
      userExperience?.shouldTrackUsage && userExperience?.events?.OPEN_ITEM
    );
  };

  const openItemTitleRef = useRef();
  //Post OPEN_ITEM when viewing a topic from the default tab, and not already posted
  useEffect(() => {
    setIsTracking(true);
    setIsInactive(false);
    if (
      shouldTrackOpenItem() &&
      pageTitle !== openItemTitleRef.current &&
      !tabType
    ) {
      postItemViews(
        dispatch,
        slugPubType,
        slugTitle,
        [
          {
            eventType: "OPEN_ITEM",
            itemName: pageTitle,
            prevSearchTerm: getSearchTerm(),
            itemPublicationDate: getLastUpdateDate(updates),
          },
        ],
        eventBus
      );

      itemviewsTimerRef.current && clearTimeout(itemviewsTimerRef.current);
      itemviewsTimerRef.current = null;
      openItemTitleRef.current = pageTitle;
    }
  }, [userExperience, tabType, pageTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  //Post LEAVE_ITEM event when inactive
  useEffect(() => {
    if (
      userExperience?.shouldTrackUsage &&
      userExperience?.events?.LEAVE_ITEM &&
      isTracking &&
      isInactive
    ) {
      postItemViews(
        dispatch,
        slugPubType,
        slugTitle,
        [
          ...userHistory.itemviews,
          {
            eventType: "LEAVE_ITEM",
            itemName: pageTitle,
            prevSearchTerm: getSearchTerm(),
            itemPublicationDate: getLastUpdateDate(updates),
          },
        ],
        eventBus
      );
      itemviewsTimerRef.current && clearTimeout(itemviewsTimerRef.current);
      itemviewsTimerRef.current = null;
    }
  }, [dispatch, isTracking, isInactive, eventBus]); // eslint-disable-line react-hooks/exhaustive-deps

  //Post LEAVE_ITEM event when loading a new topic
  const previousSlugPubType = useRef(slugPubType);
  const previousSlugTitle = useRef(slugTitle);
  const leaveItemTitleRef = useRef(pageTitle);
  const userExperienceRef = useRef(userExperience);
  useEffect(() => {
    userExperienceRef.current = userExperience;
    const pathSplit = history?.location?.pathname?.split("/");

    return () => {
      if (
        userExperienceRef.current?.shouldTrackUsage &&
        userExperienceRef.current?.events?.LEAVE_ITEM &&
        isTracking &&
        previousSlugTitle.current !== pathSplit[2]
      ) {
        postItemViews(
          dispatch,
          previousSlugPubType.current,
          previousSlugTitle.current,
          [
            ...userHistory.itemviews,
            {
              eventType: "LEAVE_ITEM",
              itemName: leaveItemTitleRef.current,
              prevSearchTerm: getSearchTerm(),
              itemPublicationDate: getLastUpdateDate(updates),
            },
          ],
          eventBus
        );
        itemviewsTimerRef.current && clearTimeout(itemviewsTimerRef.current);
        itemviewsTimerRef.current = null;
      }
      leaveItemTitleRef.current = pageTitle;
      previousSlugPubType.current = slugPubType;
      previousSlugTitle.current = slugTitle;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle, slugTitle, slugPubType, eventBus]);

  useEffect(() => {
    if (tabType !== tabTypes.VIDEOS) {
      setActiveVideo(dispatch, null);
    }
  }, [dispatch, tabType]);

  const tocForPrint = isDetailsTab ? drugDetails?.toc : toc;

  const getTocForContentPrintDialog = () => {
    if (tabType === undefined) {
      return toc;
    } else if (isDetailsTab) {
      return detailsTocForPrint;
    } else {
      return checklist;
    }
  };

  const {
    detailsTocForPrint,
    selectedReferences,
    setSelectedReferences,
    isPrintDialogOpen,
    setIsPrintDialogOpen,
    onPrint,
    handlePrintDialogParentOptionChange,
    handlePrintDialogChildOptionChange,
    handlePrintDialogSelectAll,
    handlePrintDialogDeselectAll,
    handlePrintDialogCancel,
    tocReferences,
    handleInitialPrintDialogSelections,
  } = usePrint(
    tocForPrint,
    checklist,
    tabType,
    isDetailsTab
      ? detailsSectionCache[activeDetailsSectionIndex][
          activeDetailsSubSectionIndex
        ]
      : contentSectionCache[activeSectionIndex],
    targetId,
    isDetailsTab
  );

  useEffect(() => {
    if (disablePrint) {
      handlePrintDialogDeselectAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPrintFailed, notification?.isOpen]);

  const isPreSelectAll = !!userExperience.preSelectAllSections;
  const canSelectSubsectionsForPrint = !!userExperience.canSelectSubsectionsForPrint;
  const preSelectRef = useRef(true);
  useEffect(() => {
    if (isPrintDialogOpen) {
      if (isPreSelectAll && preSelectRef.current) {
        setSelectedReferences(tocReferences);
        preSelectRef.current = false;
      } else {
        isValidSplitContent && handleInitialPrintDialogSelections();
      }
    } else {
      preSelectRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPrintDialogOpen,
    isPreSelectAll,
    tocReferences,
    targetId,
    isValidSplitContent,
  ]);

  const {
    isShareDialogOpen,
    handleShareDialogOpen,
    handleShareDialogCancel,
  } = useShare();
  const {
    hfcData,
    isHFCDialogOpen,
    handleHFCDialogOpen,
    handleHFCDialogCancel,
  } = useHFC(ancillaryData);
  const contentColumnRef = useRef();
  const [elementScroll, setElementScroll] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [tableData, setTableData] = useState({});
  useEventBusJail(
    eventBus,
    isDialogOpen,
    hasData,
    tablesContent,
    isNonFreePreview,
    setTableData,
    setIsDialogOpen,
    openTableCTA,
    dispatch
  );
  const [isEvidenceBottomSheetOpen, setIsEvidenceBottomSheetOpen] = useState(
    false
  );

  const memoizedTableData = useMemo(() => tableData, [tableData]);

  const [ddToolsData, relatedTopicsData] = useMemo(() => {
    const toolsMap = ancillaryData.reduce((acc, tool) => {
      acc[tool.type] = tool;
      return acc;
    }, {});

    const ddToolsData = toolsMap["DDToolsRightRailSection"];
    const relatedTopicsData = toolsMap["RelatedDMTopicsRightRailSection"];
    return [ddToolsData, relatedTopicsData];
  }, [ancillaryData]);

  const shouldRenderDDToolsSection = shouldRenderExtraContentSection(
    tabType,
    ddToolsData
  );
  const shouldRenderDDToolsOnMobile = !isDesktop && shouldRenderDDToolsSection;
  const shouldRenderDDToolsOnDesktop = isDesktop && shouldRenderDDToolsSection;

  const shouldRenderRelatedTopicsSection = shouldRenderExtraContentSection(
    tabType,
    relatedTopicsData
  );
  const shouldRenderRelatedTopicsOnMobile =
    !isDesktop && shouldRenderRelatedTopicsSection;

  const citeTool = headerTools?.find((tool) => tool.action === "cite");

  const [rightRailWidth, setRightRailWidth] = useState();
  const rightRailColumnRef = useCallback(
    (node) => {
      if (node && windowSize) {
        setRightRailWidth(node.getBoundingClientRect().width);
      }
    },
    [windowSize]
  );

  const handleShareActions = useCallback(() => {
    handleShareDialogOpen();
    logTopicShareClicked({
      title: pageTitle,
      recordAn: metadataAn,
    });
  }, [pageTitle, metadataAn, handleShareDialogOpen]);

  const handleCiteActions = useCallback(() => {
    setIsCitationDialogOpen(true);
    logTopicCiteClicked({
      title: pageTitle,
      recordAn: metadataAn,
    });
  }, [pageTitle, metadataAn]);

  const handlePrintActions = useCallback(() => {
    setIsPrintDialogOpen(true);
    logTopicPrintClicked({
      title: pageTitle,
      recordAn: metadataAn,
    });
  }, [pageTitle, metadataAn]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSectionByIndex = useCallback(
    (index) => {
      const section = content[index];
      if (!section) {
        return;
      }

      const newHash = `#${section.id}`;
      hashRef.current = newHash; // Makes it so that an effect doesn't try to "correct" scroll position
      setHash(newHash, pathname, history);
    },
    [content, pathname, history]
  );

  const { openTableById } = useTables({
    tablesContent,
    setIsDialogOpen,
    setTableData,
  });

  const handleGetFullTopic = useCallback(
    () =>
      getFullTopic(
        dispatch,
        contentSectionCache,
        slugPubType,
        slugTitle,
        toc,
        tabType
      ),
    [dispatch, contentSectionCache, slugPubType, slugTitle, tabType, toc]
  );

  useScrollElement(({ currPos }) => {
    setElementScroll(currPos);
  }, contentColumnRef);

  const hashRef = useRef("");
  const pathnameRef = useRef(pathname);

  const sectionNameRef = useRef();

  const handleScrollTo = (id) => {
    //If navigating to a top level section within the content split experience (i.e. a new page), scroll back to the top. Otherwise, scroll to the related sub-section.
    const isTopLevelSection = toc.some((item) => item.reference === id);

    if (isTopLevelSection && isValidSplitContent) {
      window.scrollTo(0, 0);
    } else {
      scrollToElementById(id, isHeaderFixed, isMobile, isTablet);
    }
  };

  const handleTocNavigation = (id, path, hist) => {
    if (drugMobileTocActiveTab === drugMobileTocTabTypes.IN_DEPTH_ANSWERS) {
      if (pathSectionId === id) {
        window.scrollTo(0, 0);
      } else {
        if (pathSectionId) {
          getDetailsSectionByHash({
            dispatch,
            slugPubType,
            slugTitle,
            detailsSectionCache,
            id,
            isNonFreePreview,
          });
        }
        history.push(`/${slugPubType}/${slugTitle}/${tabTypes.DETAILS}/${id}`);
      }
      //If navigating to the active/current hash, scroll back to the top of that section, otherwise propagate a hash change.
    } else if (hashRef.current === `#${id}`) {
      handleScrollTo(id);
    } else {
      if (isDetailsTab) {
        path = `/${slugPubType}/${slugTitle}`;
      }
      setHash(id, path, hist);
    }
  };

  const handleTocItemClick = (id, title, tocPath, path, hist) => {
    setActiveTocItem(dispatch, id);
    handleTocNavigation(id, path, hist);
    logTocClicked({
      title: pageTitle,
      sectionTitle: title,
      path: tocPath,
      recordAn: metadata.an,
    });
  };

  const handleResumeTracking = (
    reference,
    pageTitle,
    breadcrumb,
    pathname,
    history
  ) => {
    setIsTracking(true);
    setIsInactive(false);

    handleTocItemClick(reference, pageTitle, breadcrumb, pathname, history);
  };

  const handleDrugDetailNav = (id, title, tocPath, path, hist) => {
    if (!id) {
      return;
    }
    getDetailsSectionByHash({
      dispatch,
      slugPubType,
      slugTitle,
      detailsSectionCache,
      id,
      isNonFreePreview,
    });
    const newPathname = getDetailsSectionPath(id, path);
    newPathname && hist.push(newPathname);

    logTocClicked({
      title: pageTitle,
      sectionTitle: title,
      path: tocPath,
      recordAn: metadata.an,
    });

    window.scrollTo(0, 0);
  };

  const handleContentClick = (e) => {
    if (
      findOnPageMode === findOnPageModes.findInSection &&
      e.target.classList.contains("DMX_a")
    ) {
      logJumpLinkClicked({ title: pageTitle });
    }
  };

  const openUpdatesDialog = () => setIsUpdatesDialogOpen(true);
  const closeUpdatesDialog = () => setIsUpdatesDialogOpen(false);

  // Detect changes in the URL hash or new content/section fetching request.
  useEffect(() => {
    //When a new topic is loaded, reset the state of the hash ref.
    if (pathnameRef.current !== pathname) {
      pathnameRef.current = pathname;
      hashRef.current = null;
    }

    //Content Split: When fetching a new section/page, scroll back to the top first while the section is fetching.
    if (isContentFetching && isValidSplitContent) {
      window.scrollTo(0, 0);
    }

    //Content Split: After a new section is fetched, scroll to the hash target and set the active TOC item
    if (isValidSplitContent) {
      if (!isContentFetching && hasContentFetched) {
        setTimeout(() => {
          handleScrollTo(hash?.slice(1));
          setActiveTocItem(dispatch, hash?.slice(1));
        }, 0);
      }
    }

    if (hashRef.current !== hash && hasContentFetched) {
      setTimeout(() => {
        handleScrollTo(hash?.slice(1));
      }, 0);
      hashRef.current = hash;
    }
  }, [hash, hasContentFetched, isContentFetching, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const reloadAfterFiveSeconds = setTimeout(() => {
      const {
        search,
        pathname: historyPath,
        hash: historyHash,
        state,
      } = history.location;
      const rebuiltPath = `${historyPath}${search}${historyHash}`;

      history.replace(rebuiltPath, {
        ...state,
        shouldReloadRecentlyViewed: true,
      });
    }, 5000);
    return () => {
      clearTimeout(reloadAfterFiveSeconds);
    };
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasPrefetchedTopic = useRef(false);
  useEffect(() => {
    const preFetchFullTopic = setTimeout(() => {
      if (
        isValidSplitContent &&
        !isPreviewMode &&
        !hasPrefetchedTopic.current
      ) {
        handleGetFullTopic();
        hasPrefetchedTopic.current = true;
      }
    }, 5000);

    return () => {
      clearTimeout(preFetchFullTopic);
    };
  }, [isValidSplitContent, isPreviewMode, handleGetFullTopic]);

  const tableTitleRef = useRef("");
  useEffect(() => {
    if (isDialogOpen) {
      const {
        isStandAlone,
        title: tableTitle,
        tableId,
        affordance,
        navigateFrom,
      } = memoizedTableData;
      tableTitleRef.current = tableTitle;
      logTableViewed({
        title: pageTitle,
        isStandAlone,
        tableTitle,
        tableId,
        affordance,
        recordAn: metadataAn,
        navigateFrom,
      });
    }
  }, [isDialogOpen, memoizedTableData, pageTitle, metadataAn]);

  useEffect(() => {
    if (isNonFreePreview) {
      if (
        tabTypes.IMAGES !== tabType &&
        tabTypes.VIDEOS !== tabType &&
        tabTypes.ALGORITHMS !== tabType &&
        tabTypes.TABLES !== tabType
      ) {
        launchCTA({ userAction: CTA_TRIGGER_ACTION.TOPIC_VIEW });
      }
      setTimeout(() => {
        if (contentSections && contentSections.length > 0 && !tabType) {
          const id = contentSections[0].id;
          setHash("#" + id, pathname, history);
        }
      }, 2000);
    }
  }, [
    isNonFreePreview,
    launchCTA,
    contentSections,
    pathname,
    history,
    tabType,
  ]);

  // Logic to keep track if is navigating to a new slug
  // This is used for prevent getSection from calling when navigate to a different topic
  // as getContent take priority
  const previousPubTyeRef = useRef();
  const previousSlugTitleRef = useRef();
  const isSameSlugRef = useRef(false);
  useEffect(() => {
    isSameSlugRef.current =
      previousPubTyeRef.current === slugPubType &&
      previousSlugTitleRef.current === slugTitle;

    return () => {
      previousPubTyeRef.current = slugPubType;
      previousSlugTitleRef.current = slugTitle;
    };
  }, [slugPubType, slugTitle, targetId]);

  const shouldFetchSection = useCallback(
    (originatorId) => {
      return (
        isValidSplitContent &&
        idToSectionMap &&
        targetId &&
        targetId !== originatorId
      );
    },
    [targetId, isValidSplitContent, idToSectionMap]
  );

  const currentSectionIndex = idToSectionMap?.[targetId]?.index ?? 0;
  const sectionFromCache = contentSectionCache[currentSectionIndex];

  useEffect(() => {
    const isNavigatingFromDetailsTab =
      idToSectionMap &&
      !!activeSectionIndex !== idToSectionMap?.[drugDetails?.activeSectionId];

    if (
      isSameSlugRef.current &&
      (shouldFetchSection(lastHash) || isNavigatingFromDetailsTab)
    ) {
      getSectionByHash({
        dispatch,
        slugPubType,
        slugTitle,
        sectionFromCache,
        idToSectionMap,
        toc,
        targetId,
        isNonFreePreview,
        drugMobileTocActiveTab,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, slugPubType, slugTitle, isNonFreePreview, dispatch]);

  // This useEffect is to load sections for print
  useEffect(() => {
    if (isValidSplitContent) {
      if (isDetailsTab) {
        const filteredSectionIds = selectedReferences.filter(
          (sectionId) =>
            !!drugDetails?.sectionMap[sectionId] &&
            !detailsSectionCache[
              drugDetails?.sectionMap[sectionId].sectionIndex
            ][drugDetails?.sectionMap[sectionId].subSectionIndex]
        );
        if (filteredSectionIds?.length > 0) {
          getPrintDetailsSections(
            dispatch,
            slugPubType,
            slugTitle,
            filteredSectionIds
          );
        }
      } else {
        // This logic get the section index of all selectedReferences,
        // filter out invalid value and dedupe
        const printingSections = [
          ...new Set(
            selectedReferences
              .map((reference) => {
                const parentIndex = idToSectionMap[reference]?.index ?? -1;
                if (parentIndex > -1) {
                  return toc[parentIndex].reference;
                }
                return undefined;
              })
              .filter((reference) => reference)
          ),
        ];

        // Filter out sections where content is already loaded
        const sectionsForRetrieval = printingSections.filter(
          (section) => !contentSectionCache[idToSectionMap[section].index]
        );

        // Create string with list of section ID to call experience with
        if (sectionsForRetrieval?.length > 0) {
          const sectionRefs = sectionsForRetrieval.reduce(
            (value, currentValue) => `${value},${currentValue}`
          );
          getPrintSections(dispatch, slugPubType, slugTitle, sectionRefs);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReferences, dispatch]);

  useEffect(() => {
    if (isMobileTocOpen) {
      const closeMobileTocButton = document.getElementById(
        "mobile-toc-close-button"
      );
      if (closeMobileTocButton) {
        closeMobileTocButton.focus();
      }
    }
  }, [isMobileTocOpen]);

  const openWithCloudinaryToken = async (originalUrl) => {
    const epic = new Epic();
    if (epic.isEpic()) {
      const resolveToken = () => {
        if (cloudinaryToken) {
          return cloudinaryToken;
        }
        return getCloudinaryToken(dispatch).catch(() => "");
      };
      const token = await resolveToken();
      const url = token ? `${originalUrl}?__cld_token__=${token}` : originalUrl;
      epic.openExternalWindow(url);
    } else {
      window.open(originalUrl, "_blank").focus();
    }
  };

  useMerativeLink();

  if (isLoading) {
    return <MainSpinner />;
  }

  const findOnPageButtonDOM = document.getElementById("toolbar-btn");

  const openMobileToc = () => {
    setMobileTocOpen(true);
    if (hasDrugMobileTocTabs) {
      if (tabType !== tabTypes.DETAILS) {
        sectionNameRef.current = handleGetSectionName();
      }
      if (!drugDetails?.toc) {
        getContent(dispatch, slugPubType, slugTitle, tabTypes.DETAILS);
      }
    }
    document.body.classList.add("RCL_PinBody__menu");
  };

  const setPathForDrugMobileTocClose = () => {
    const basePath = `/${slugPubType}/${slugTitle}`;
    let newPath;
    if (
      tabType !== tabTypes.DETAILS &&
      drugMobileTocActiveTab === drugMobileTocTabTypes.IN_DEPTH_ANSWERS
    ) {
      if (sectionNameRef.current !== null) {
        newPath = `${basePath}/${tabTypes.DETAILS}/${sectionNameRef.current}`;
      } else {
        newPath = `${basePath}/${tabTypes.DETAILS}`;
      }
    } else if (
      isDetailsTab &&
      drugMobileTocActiveTab !== drugMobileTocTabTypes.IN_DEPTH_ANSWERS
    ) {
      if (drugDetails?.activeSectionId) {
        newPath = `${basePath}#${drugDetails.activeSectionId}`;
      } else {
        newPath = basePath;
      }
    } else {
      // do nothing except keep sonar happy
    }
    if (newPath) {
      history.push(newPath);
    }
  };

  const closeMobileToc = () => {
    if (hasDrugMobileTocTabs) {
      setPathForDrugMobileTocClose();
    }
    setMobileTocOpen(false);
    document.body.classList.remove("RCL_PinBody__menu");
  };

  const handleMobileTocItemClick = (id, title, breadcrumb) => {
    handleTocItemClick(id, title, breadcrumb, pathname, history);
    setMobileTocOpen(false);
    document.body.classList.remove("RCL_PinBody__menu");
  };

  const pageHeading = (
    <Type
      data-testid="content-page-topic-title"
      element="h1"
      styledAs={isMobileTocOpen ? "h3" : "h1"}
      color="gray800"
      fontFamily="secondary"
    >
      {pageTitle}
    </Type>
  );

  const boxedWarningProps = {
    drugTitle: header?.title?.label,
    isBoxedWarningOpen,
    setIsBoxedWarningOpen,
    boxedWarnings: header?.boxedWarnings,
    telemetryData: {
      title: pageTitle,
      pubType: prettyPubType,
      recordAn: metadataAn,
      affordance: affordances.DRUG_MONOGRAPH,
    },
    isPreviewMode: userExperience?.isPreviewMode,
  };

  const ddToolsOnMobileView = (
    <Flex
      container
      justify={{
        content: "center",
      }}
      marginTop="sm"
    >
      <ContentRightRailDDTools
        telemetryData={{
          pageTitle,
          recordAn: metadataAn,
          pubType: prettyPubType,
          affordance: isMobile
            ? affordances.TOPIC_BOTTOM
            : affordances.TOPIC_RIGHT,
        }}
        {...ddToolsData}
      />
    </Flex>
  );

  const relatedTopicsOnMobileView = (
    <Flex container marginTop="md">
      <RightRailRelatedTopics {...relatedTopicsData} />
    </Flex>
  );

  if (!hasData) {
    return (
      <Wrapper data-testid="content-page-root">
        <NoDataMessage message={noDataMessage} />
      </Wrapper>
    );
  }

  let proportionalTablesContent = tablesContent;
  if (tablesContent) {
    proportionalTablesContent = setTableWidths(tablesContent);
  }

  const activeTab = tabs.find((tab) => tab.name === tabType) ?? tabs[0];
  const activeTabPath = activeTab.path;
  const activeTabIndex = tabs?.findIndex((tab) => tab.path === activeTabPath);

  return (
    <Wrapper data-testid="content-page-root">
      <CommonComponents />
      {multimedia?.images && (
        <>
          <DialogLightBoxImage
            type="image"
            images={multimedia.images}
            currentImage={currentImage}
            onNextImage={handleNextImage}
            onPrevImage={handlePrevImage}
            isFullSizeOpen={fullSizeImageModalState}
            onFullSizeModalToggle={fullSizeImageModalHandle}
            isOpen={carouselImageModalState}
            onRequestClose={carouselImageModalHandle}
            affordance={affordanceState}
          />
        </>
      )}
      {multimedia?.videos && (
        <>
          <DialogLightBoxVideo
            videos={multimedia.videos}
            currentVideo={currentVideo}
            onNextVideo={handleNextVideo}
            onPrevVideo={handlePrevVideo}
            isOpen={carouselVideoModalState}
            onRequestClose={carouselVideoModalHandle}
            affordance={affordanceState}
          />
        </>
      )}
      {isMobileTocOpen && (
        <TocMobileOverlay data-testid="mobile-toc-root">
          <TocMobileNameHolder>
            {pageHeading}
            <TocMobileCloseButton
              aria-label="close"
              data-testid="mobile-toc-close-button"
              variant="quinary"
              kind="contained"
              size="sm"
              onClick={closeMobileToc}
              id="mobile-toc-close-button"
            >
              <Icon
                icon={{
                  glyph: "close",
                }}
              />
            </TocMobileCloseButton>
          </TocMobileNameHolder>
          {hasDrugMobileTocTabs && (
            <DrugMobileTocTabs tabs={tabs} activeTab={drugMobileTocActiveTab} />
          )}
          <TocMobileWrapper>
            <ContentToc
              dataTestid="content-page-mobile-toc"
              handleTocItemClick={handleMobileTocItemClick}
              toc={
                drugMobileTocActiveTab ===
                drugMobileTocTabTypes.IN_DEPTH_ANSWERS
                  ? drugDetails.toc
                  : toc
              }
              activeReference={
                drugMobileTocActiveTab ===
                drugMobileTocTabTypes.IN_DEPTH_ANSWERS
                  ? drugDetails?.activeSectionId
                  : activeTocItem
              }
            />
          </TocMobileWrapper>
        </TocMobileOverlay>
      )}
      {header && (
        <ContentHeader
          {...header}
          isHeaderFixed={isHeaderFixed}
          slugPubType={slugPubType}
          slugTitle={slugTitle}
          workplaceData={workplaceData}
          breadcrumbData={
            isDetailsTab ? drugDetails?.breadcrumbData ?? [] : breadcrumbData
          }
          handleTocItemClick={handleTocItemClick}
          onBreadcrumbClick={handleTocNavigation}
          onTopicUpdatesLinkClick={openUpdatesDialog}
          pubTypeLogos={pubTypeLogos}
          previousAddress={
            isDetailsTab ? previousDetailsSectionId : previousSectionHash
          }
          nextAddress={isDetailsTab ? nextDetailsSectionId : nextSectionHash}
          openWithCloudinaryToken={openWithCloudinaryToken}
          previousHref={
            isDetailsTab
              ? getDetailsSectionPath(previousDetailsSectionId, pathname)
              : `#${previousSectionHash}`
          }
          nextHref={
            isDetailsTab
              ? getDetailsSectionPath(nextDetailsSectionId, pathname)
              : `#${nextSectionHash}`
          }
          isValidSplitContent={isValidSplitContent}
          isMobile={isMobile}
          isDesktop={isDesktop}
          activeSectionIndex={activeSectionIndex}
          toc={isDetailsTab ? drugDetails.toc ?? [] : toc}
          openMobileToc={openMobileToc}
          pageHeading={pageHeading}
          telemetryData={{
            title: pageTitle,
            sectionTitle: activeSectionTitle,
            recordAn: metadataAn,
          }}
          urlFragment={isDetailsTab ? `${location.pathname}/` : "#"}
          currentDmxDrugTab={currentDmxDrugTab}
          history={history}
          handleDrugDetailNav={handleDrugDetailNav}
          location={location}
          isDetailsTab={isDetailsTab}
          drugInteractionEvent={userExperience?.drugInteractionEvent}
          isPreviewMode={userExperience?.isPreviewMode}
          pdfFiles={pdfFiles}
          crossContentReferenceLinks={crossContentReferenceLinks}
          boxedWarningProps={boxedWarningProps}
          contributors={about?.contributors}
          isContributorNamesDialogEnabled={
            userExperience.isContributorNamesDialogEnabled
          }
          productName={productName}
        />
      )}
      {workplaceData?.notes?.displayData && (
        <ContentNote
          displayedContent={workplaceData.notes.displayData.content}
          displayData={workplaceData.notes.displayData}
        />
      )}
      {workplaceData?.notes?.shouldShowAdminWidget && (
        <span data-testid="admin-widget-container">
          <ModuleLoader
            containerName={adminWidgetConfig?.containerName}
            module={adminWidgetConfig?.module}
            remoteUrl={adminWidgetConfig?.remoteUrl}
            titleLabel={pageTitle}
            slugPubtype={slugPubType}
            slugTitle={slugTitle}
          />
        </span>
      )}
      <ContentHeaderBar
        actions={{
          follow: () => handleFollowIconClick(),
          unfollow: () => handleUnfollowActions(),
          print: () => handlePrintActions(),
          share: () => handleShareActions(),
          bookmark: () =>
            !isBookmarking && handleBookmarkActions(bookmarkContent),
          unbookmark: () =>
            !isBookmarking && handleBookmarkActions(unbookmarkContent),
          cite: () => handleCiteActions(),
        }}
        tools={headerTools}
        tabs={tabs}
        recordAn={metadataAn}
        isMobile={isMobile}
        getSectionName={handleGetSectionName}
        activeDrugDetailSection={drugDetails?.activeSectionId}
        telemetryData={{
          title: pageTitle,
          sectionTitle: activeSectionTitle,
          recordAn: metadataAn,
          affordance: affordances.PAGE_TOP,
        }}
        activeTabIndex={activeTabIndex}
        activeTabPath={activeTabPath}
      />
      {tabType === tabTypes.IMAGES && (
        <TabPanel
          active
          id={`${activeTab.testId}-panel`}
          aria-labelledby={activeTab.testId}
        >
          <Flex container>
            <Flex cell span={12}>
              <MediaGallery
                mediaItems={multimedia?.images}
                type="images"
                handleOpenLightBox={openImageHandler}
                isMobileDevice={isMobile}
                isTabletDevice={isTablet}
              />
            </Flex>
          </Flex>
        </TabPanel>
      )}
      {tabType === tabTypes.VIDEOS && (
        <TabPanel
          active
          id={`${activeTab.testId}-panel`}
          aria-labelledby={activeTab.testId}
        >
          <Flex container>
            <Flex cell span={12}>
              <MediaGallery
                mediaItems={multimedia?.videos}
                type="videos"
                handleOpenLightBox={openVideoHandler}
                isMobileDevice={isMobile}
                isTabletDevice={isTablet}
              />
            </Flex>
          </Flex>
        </TabPanel>
      )}
      {tabType === tabTypes.ALERTS && (
        <TabPanel
          active
          id={`${activeTab.testId}-panel`}
          aria-labelledby={activeTab.testId}
        >
          <Flex container noGap>
            <FlexUpdates cell span={isDesktop ? 10 : 12}>
              <UpdatesTab updates={updates} />
            </FlexUpdates>
          </Flex>
        </TabPanel>
      )}
      {tabType === tabTypes.ABOUT && (
        <TabPanel
          active
          id={`${activeTab.testId}-panel`}
          aria-labelledby={activeTab.testId}
        >
          <Flex container>
            <Flex cell span={12}>
              <AboutTab about={about} isDesktop={isDesktop} />
            </Flex>
          </Flex>
        </TabPanel>
      )}
      {tabType === tabTypes.CHECKLIST && (
        <TabPanel
          active
          id={`${activeTab.testId}-panel`}
          aria-labelledby={activeTab.testId}
        >
          <Flex column={!isDesktop} container noGap>
            <FlexChecklistInfoColumn cell span={isDesktop ? 2 : 12}>
              <StyledInfoText
                children={
                  "<span>The printable version of this checklist contains a form to allow you to record evaluator and examinee names and signatures, date and pass/fail status.</span>"
                }
              />
            </FlexChecklistInfoColumn>
            <FlexContentColumn noGap cell span={isDesktop ? 10 : 12}>
              <ChecklistTab
                checklist={checklist}
                selectedReferences={selectedReferences}
              />
            </FlexContentColumn>
          </Flex>
        </TabPanel>
      )}
      {tabType === tabTypes.ALGORITHMS && (
        <TabPanel
          active
          id={`${activeTab.testId}-panel`}
          aria-labelledby={activeTab.testId}
        >
          <Flex container>
            <Flex cell span={12}>
              <AlgorithmsTab
                handleCTA={openAlgorithmCTA}
                handleOpenPDF={openWithCloudinaryToken}
                isNonFreePreview={isNonFreePreview}
                mediaItems={multimedia?.algorithms}
                slugPubType={slugPubType}
                slugTitle={slugTitle}
              />
            </Flex>
          </Flex>
        </TabPanel>
      )}
      {tabType === tabTypes.TABLES && (
        <TabPanel
          active
          id={`${activeTab.testId}-panel`}
          aria-labelledby={activeTab.testId}
        >
          <Flex container>
            <Flex cell span={12}>
              <TablesTab
                tablesContent={proportionalTablesContent}
                ancillaryData={ancillaryData}
                setIsDialogOpen={setIsDialogOpen}
                setTableData={setTableData}
                slugTitle={slugTitle}
                slugPubType={slugPubType}
                isNonFreePreview={isNonFreePreview}
                handleOpenTable={openTableCTA}
              />
            </Flex>
          </Flex>
        </TabPanel>
      )}
      {isDetailsTab && (
        <TabPanel
          active
          id={`${activeTab.testId}-panel`}
          aria-labelledby={activeTab.testId}
        >
          <FlexContainer container noGap>
            <FlexTocColumn cell span={3}>
              {drugDetails?.toc && (
                <TocWrapperDiv
                  windowSize={windowSize}
                  isHeaderFixed={isHeaderFixed}
                  elementScroll={elementScroll}
                  rightRailWidth={rightRailWidth}
                >
                  <ContentToc
                    dataTestid={"content-page-details-toc"}
                    handleTocItemClick={handleDrugDetailNav}
                    toc={drugDetails.toc}
                    activeReference={drugDetails?.activeSectionId}
                  />
                </TocWrapperDiv>
              )}
            </FlexTocColumn>
            {detailsContent && (
              <FlexContentColumn noGap cell span={isDesktop ? 6 : 12}>
                {!isPrintDialogOpen && isDetailsSectionFetching ? (
                  <ContentSectionSkeleton />
                ) : (
                  <ContentBlockWrapper
                    ref={contentColumnRef}
                    onClick={handleContentClick}
                  >
                    <Flex
                      container
                      align={{
                        content: "space-between",
                      }}
                    >
                      {(isMobile || isTablet) && (
                        <TopicMenuButton
                          data-testid="mobile-toc-open-button"
                          variant="quinary"
                          padded={false}
                          kind="contained"
                          size="md"
                          onClick={openMobileToc}
                        >
                          <Icon
                            icon={{
                              size: "md",
                              glyph: "menu",
                            }}
                          />
                          Topic Menu
                        </TopicMenuButton>
                      )}
                      {drugDetails?.toc?.length && (
                        <Flex container justify={{ content: "flex-end" }}>
                          <PreviousNextSection
                            previousAddress={previousDetailsSectionId}
                            nextAddress={nextDetailsSectionId}
                            previousHref={getDetailsSectionPath(
                              previousDetailsSectionId,
                              pathname
                            )}
                            nextHref={getDetailsSectionPath(
                              nextDetailsSectionId,
                              pathname
                            )}
                            isMobile={isMobile}
                            telemetryData={{
                              title: pageTitle,
                              affordance: affordances.PAGE_TOP,
                            }}
                            history={history}
                            handleDrugDetailNav={handleDrugDetailNav}
                            location={location}
                            isDetailsTab={isDetailsTab}
                          />
                        </Flex>
                      )}
                    </Flex>
                    <DetailsBlock
                      isDetailsSectionFetching={isDetailsSectionFetching}
                      detailsContent={detailsContent}
                      detailsSectionIndex={activeDetailsSectionIndex}
                      detailsSubSectionIndex={activeDetailsSubSectionIndex}
                      isDesktopDevice={isDesktop}
                      isMobileDevice={isMobile}
                      isTabletDevice={isTablet}
                      detailsToc={drugDetails?.toc}
                      slugPubtype={slugPubType}
                      slugTitle={slugTitle}
                      detailsSectionCache={detailsSectionCache}
                      sectionMap={drugDetails?.sectionMap}
                      selectedReferences={selectedReferences}
                      drugConsultUrlTemplate={
                        userExperience?.drugConsultUrlTemplate
                      }
                      dispatch={dispatch}
                      telemetryData={{
                        title: pageTitle,
                        recordAn: metadataAn,
                      }}
                    />
                    {drugDetails?.toc?.length && (
                      <Flex
                        container
                        justify={{
                          content: "center",
                        }}
                      >
                        <PreviousNextSection
                          previousAddress={previousDetailsSectionId}
                          nextAddress={nextDetailsSectionId}
                          previousHref={getDetailsSectionPath(
                            previousDetailsSectionId,
                            pathname
                          )}
                          nextHref={getDetailsSectionPath(
                            nextDetailsSectionId,
                            pathname
                          )}
                          isMobile={isMobile}
                          telemetryData={{
                            title: pageTitle,
                            affordance: affordances.PAGE_BOTTOM,
                          }}
                          isPageBottom
                          history={history}
                          handleDrugDetailNav={handleDrugDetailNav}
                          location={location}
                          isDetailsTab={isDetailsTab}
                        />
                      </Flex>
                    )}
                  </ContentBlockWrapper>
                )}
              </FlexContentColumn>
            )}

            {shouldRenderRelatedTopicsOnMobile && relatedTopicsOnMobileView}
            {shouldRenderDDToolsOnDesktop && (
              <FlexRightRailColumn noGap cell span={3}>
                <ContentPageRightRail>
                  <ContentRightRailDDTools
                    telemetryData={{
                      pageTitle,
                      recordAn: metadataAn,
                      pubType: prettyPubType,
                      affordance: isMobile
                        ? affordances.TOPIC_BOTTOM
                        : affordances.TOPIC_RIGHT,
                    }}
                    {...ddToolsData}
                  />
                </ContentPageRightRail>
              </FlexRightRailColumn>
            )}

            {shouldRenderDDToolsOnMobile && ddToolsOnMobileView}
          </FlexContainer>
        </TabPanel>
      )}
      {tabType === undefined && (
        <TabPanel
          active
          id={`${activeTab.testId}-panel`}
          aria-labelledby={activeTab.testId}
        >
          <FlexContainer container noGap>
            <FlexTocColumn cell span={3}>
              <TocWrapperDiv
                windowSize={windowSize}
                isHeaderFixed={isHeaderFixed}
                elementScroll={elementScroll}
                rightRailWidth={rightRailWidth}
              >
                <ContentToc
                  dataTestid={"content-page-content-toc"}
                  handleTocItemClick={handleTocItemClick}
                  toc={toc}
                  closeSections={true}
                  loadedSection={loadedSection}
                  activeReference={activeTocItem}
                />
              </TocWrapperDiv>
            </FlexTocColumn>
            {(!isValidSplitContent || contentSectionCache?.length > 0) && (
              <FlexContentColumn noGap cell span={isDesktop ? 6 : 12}>
                {!isPrintDialogOpen && isContentFetching ? (
                  <ContentSectionSkeleton />
                ) : (
                  <ContentBlockWrapper ref={contentColumnRef}>
                    <Flex
                      container
                      align={{
                        content: "space-between",
                      }}
                      data-testid="content-page-content-block-header"
                    >
                      {(isMobile || isTablet) && (
                        <TopicMenuButton
                          data-testid="mobile-toc-open-button"
                          variant="quinary"
                          padded={false}
                          kind="contained"
                          size="md"
                          onClick={openMobileToc}
                        >
                          <Icon
                            icon={{
                              size: "md",
                              glyph: "menu",
                            }}
                          />
                          Topic Menu
                        </TopicMenuButton>
                      )}
                      {isValidSplitContent && contentSections?.length > 0 && (
                        <Flex
                          container
                          justify={{
                            content: "flex-end",
                          }}
                        >
                          <PreviousNextSection
                            previousAddress={previousSectionHash}
                            nextAddress={nextSectionHash}
                            previousHref={`#${previousSectionHash}`}
                            nextHref={`#${nextSectionHash}`}
                            isMobile={isMobile}
                            telemetryData={{
                              title: pageTitle,
                              affordance: affordances.PAGE_TOP,
                            }}
                            history={history}
                            handleDrugDetailNav={handleDrugDetailNav}
                            location={location}
                            isDetailsTab={isDetailsTab}
                          />
                        </Flex>
                      )}
                    </Flex>
                    {isValidSplitContent && !content?.[activeSectionIndex] ? (
                      <ContentSectionSkeleton isAnimated={false} />
                    ) : (
                      <ContentBlock
                        content={content}
                        hfcData={hfcData}
                        tablesContent={proportionalTablesContent}
                        updates={updates}
                        handleTocItemClick={handleTocItemClick}
                        isValidSplitContent={isValidSplitContent}
                        idToSectionMap={idToSectionMap}
                        activeSectionIndex={activeSectionIndex}
                        transformData={transformData}
                        referenceData={referenceData}
                        multimedia={multimedia}
                        handleOpenPDF={openWithCloudinaryToken}
                        handleOpenLightBoxImage={openImageHandler}
                        handleOpenLightBoxVideo={openVideoHandler}
                        handleHFCDialogOpen={handleHFCDialogOpen}
                        selectedReferences={selectedReferences}
                        referenceSectionId={referenceSectionId}
                        careInterventionsSectionId={careInterventionsSectionId}
                        userExperience={userExperience}
                        setIsDialogOpen={setIsDialogOpen}
                        setTableData={setTableData}
                        ancillaryData={ancillaryData}
                        setIsEvidenceBottomSheetOpen={
                          setIsEvidenceBottomSheetOpen
                        }
                        isDesktopDevice={isDesktop}
                        isMobileDevice={isMobile}
                        isTabletDevice={isTablet}
                        loe={loe}
                        toc={toc}
                        telemetryData={{
                          title: pageTitle,
                          sectionTitle: activeSectionTitle,
                          recordAn: metadataAn,
                          pubType: prettyPubType,
                          isFreePreview: userExperience?.isPreviewMode,
                        }}
                        slugPubType={slugPubType}
                        slugTitle={slugTitle}
                      />
                    )}
                    {isValidSplitContent && contentSections?.length > 0 && (
                      <Flex
                        container
                        justify={{
                          content: "center",
                        }}
                      >
                        <PreviousNextSection
                          previousAddress={previousSectionHash}
                          nextAddress={nextSectionHash}
                          previousHref={`#${previousSectionHash}`}
                          nextHref={`#${nextSectionHash}`}
                          isMobile={isMobile}
                          telemetryData={{
                            title: pageTitle,
                            affordance: affordances.PAGE_BOTTOM,
                          }}
                          isPageBottom
                          history={history}
                          handleDrugDetailNav={handleDrugDetailNav}
                          location={location}
                          isDetailsTab={isDetailsTab}
                        />
                      </Flex>
                    )}
                    {shouldRenderRelatedTopicsOnMobile &&
                      relatedTopicsOnMobileView}
                    {shouldRenderDDToolsOnMobile && ddToolsOnMobileView}
                  </ContentBlockWrapper>
                )}
              </FlexContentColumn>
            )}
            {!userExperience?.suppressRightRail && (
              <FlexRightRailColumn noGap cell span={3}>
                <div ref={rightRailColumnRef}>
                  <ContentPageRightRail>
                    <ContentRightRail
                      ancillaryData={ancillaryData}
                      contributors={about?.contributors}
                      isContributorNamesDialogEnabled={
                        userExperience.isContributorNamesDialogEnabled
                      }
                      multimedia={multimedia}
                      handleAlgorithmCTA={openAlgorithmCTA}
                      handleOpenAlgorithmPDF={openWithCloudinaryToken}
                      handleOpenLightBoxImage={openImageHandler}
                      handleOpenLightBoxVideo={openVideoHandler}
                      handleOpenTable={openTableCTA}
                      isNonFreePreview={isNonFreePreview}
                      slugTitle={slugTitle}
                      slugPubType={slugPubType}
                      endorsers={about?.endorsers}
                      isMobile={isMobile}
                      isTablet={isTablet}
                      setIsDialogOpen={setIsDialogOpen}
                      setTableData={setTableData}
                      tablesContent={proportionalTablesContent}
                      telemetryData={{
                        pageTitle,
                        recordAn: metadataAn,
                        pubType: prettyPubType,
                        affordance: isMobile
                          ? affordances.TOPIC_BOTTOM
                          : affordances.TOPIC_RIGHTRAIL,
                      }}
                    />
                  </ContentPageRightRail>
                </div>
              </FlexRightRailColumn>
            )}
          </FlexContainer>
        </TabPanel>
      )}
      <PrintLegalText pubType={slugPubType} />
      <ContentShareDialog
        isOpen={isShareDialogOpen}
        pubType={slugPubType}
        slug={slugTitle}
        productCode={productCode}
        onCancel={handleShareDialogCancel}
      />
      <ContentHFCDialog
        isOpen={isHFCDialogOpen}
        dialogContent={hfcData?.dialog}
        dialogTitle={hfcData?.dialogTitle}
        onCancel={handleHFCDialogCancel}
      />
      <ContentPrintDialog
        isMobile={isMobile}
        telemetryData={{
          title: pageTitle,
          recordAn: metadataAn,
        }}
        isOpen={isPrintDialogOpen}
        data={getTocForContentPrintDialog()}
        onCancel={handlePrintDialogCancel}
        selectedReferences={selectedReferences}
        tocReferences={tocReferences}
        onParentChange={handlePrintDialogParentOptionChange}
        onChildChange={handlePrintDialogChildOptionChange}
        onPrint={onPrint}
        handlePrintDialogSelectAll={handlePrintDialogSelectAll}
        handlePrintDialogDeselectAll={handlePrintDialogDeselectAll}
        canSelectSubsectionsForPrint={canSelectSubsectionsForPrint}
        disablePrint={disablePrint}
      />
      <UserInactivityDialog
        isOpen={isInactive}
        onClose={() => {
          setIsInactive(false);
        }}
        handleResumeTracking={() => {
          handleResumeTracking(
            activeTocItem,
            pageTitle,
            content?.title,
            pathname,
            history
          );
        }}
      />
      <ContentTableDialog
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        tableData={tableData}
        preProcessingData={{
          referenceData,
          referenceSectionId,
          ancillaryData,
          isMobile,
          isTablet,
          loe,
        }}
        telemetryData={{
          title: pageTitle,
          pubType: prettyPubType,
          recordAn: metadataAn,
          affordance: affordances.TABLE,
          tableTitleRef,
        }}
      />
      <FollowDialog
        isOpen={isFollowDialogOpen}
        subscriptionOptions={subscriptionOptions}
        onRequestClose={() => toggleFollowDialog(false)}
        onSave={handleFollowActions}
      />
      <CitationDialog
        isCitationDialogOpen={isCitationDialogOpen}
        setIsCitationDialogOpen={setIsCitationDialogOpen}
        info={citeTool?.data?.info}
        handleCopyToClipboard={handleCopyToClipboard}
        telemetryData={{
          title: pageTitle,
          recordAn: metadataAn,
        }}
      />
      <TopicUpdatesDialog
        hash={hash}
        handleScrollTo={handleScrollTo}
        isOpen={isUpdatesDialogOpen}
        onClose={closeUpdatesDialog}
        topicBaseUrl={topicBaseUrl}
        updates={updates}
      />
      <Notification notification={notification} />
      {findOnPageButtonDOM &&
        shouldRenderFindOnPage &&
        ReactDOM.createPortal(
          <FindOnPage
            isContentFetching={isContentFetching || isDetailsSectionFetching}
            findOnPageMode={findOnPageMode}
            userExperience={userExperience}
            contentColumnRef={contentColumnRef}
            toolbarDOM={findOnPageButtonDOM.parentNode}
            isContentSplit={!!isValidSplitContent}
            getFullTopic={handleGetFullTopic}
            isFullTopicLoading={isFullTopicLoading}
            isFullTopicLoaded={isFullTopicLoaded}
            fullTopicLoadingError={fullTopicLoadingError}
            isFullTopicPreprocessing={isFullTopicPreprocessing}
            fullTopicPreprocessingError={fullTopicPreprocessingError}
            currentSectionIndex={currentSectionIndex}
            openSectionByIndex={openSectionByIndex}
            openTableById={openTableById}
            content={content}
            updates={updates}
            hfcData={hfcData}
            ancillaryData={ancillaryData}
            transformData={transformData}
            referenceData={referenceData}
            multimedia={multimedia}
            tablesContent={tablesContent}
            referenceSectionId={referenceSectionId}
            loe={loe}
            tab={tabType}
            pathSectionId={pathSectionId}
            productCode={productCode}
            telemetryData={{
              pageTitle: pageTitle,
              recordAn: metadataAn,
            }}
            slugPubType={slugPubType}
            slugTitle={slugTitle}
            isMobile={isMobile}
          />,
          findOnPageButtonDOM
        )}
      <EvidenceBottomSheet
        isBottomOpen={isEvidenceBottomSheetOpen}
        setIsBottomOpen={setIsEvidenceBottomSheetOpen}
        loeBrochureUrl={userExperience?.loeBrochureUrl}
      />{" "}
    </Wrapper>
  );
};

ContentPage.displayName = "ContentPage";

export default ContentPage;
